import { useCallback, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { applyDraftSseProgressEvent, postDraftDocumentSse } from "@/lib/draft-sse";
import { encodeSourceDocuments } from "@/lib/draft-document-utils";
import type { DraftSourceDocumentInput, DraftStreamCompletion } from "@/types/drafting";
import type { ProgressState } from "@/types";

const initialProgress: ProgressState = {
  message: "Initializing...",
  progress: 0,
  isConnected: false,
  logs: [],
};

export function useDraftGeneration() {
  const { getAccessToken } = useAuth();
  const [progress, setProgress] = useState<ProgressState>(initialProgress);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generateDraft = useCallback(
    async (
      matterId: string,
      options?: {
        sourceDocuments?: DraftSourceDocumentInput[];
        backgroundInfo?: string;
        agentInstructions?: string;
      }
    ): Promise<DraftStreamCompletion | null> => {
      setIsGenerating(true);
      setError(null);
      setProgress({
        message: "Connecting...",
        progress: 0,
        isConnected: false,
        logs: [],
      });

      try {
        const pendingUploads = options?.sourceDocuments?.filter((doc) => doc.file) ?? [];
        const sourceDocuments = pendingUploads.length
          ? await encodeSourceDocuments(
              pendingUploads.map((doc) => ({
                file: doc.file,
                docType: doc.docType,
                fileName: doc.fileName,
              }))
            )
          : undefined;

        const accessToken = await getAccessToken();
        const result = await postDraftDocumentSse(
          {
            matter_id: matterId,
            ...(options?.backgroundInfo ? { background_info: options.backgroundInfo } : {}),
            ...(options?.agentInstructions ? { agent_instructions: options.agentInstructions } : {}),
            ...(sourceDocuments?.length ? { sourceDocuments } : {}),
          },
          accessToken,
          {
            onProgress: (data) => {
              setProgress((prev) => applyDraftSseProgressEvent(prev, data));
            },
            onHeartbeat: () => {
              setProgress((prev) => ({ ...prev, isConnected: true }));
            },
          }
        );

        if (!result) {
          throw new Error("Draft generation completed without a result");
        }

        setProgress((prev) => ({
          ...prev,
          message: "Draft generated successfully",
          progress: 100,
          isConnected: true,
        }));

        return result;
      } catch (err) {
        const message = err instanceof Error ? err.message : "Draft generation failed";
        setError(message);
        setProgress((prev) => ({
          ...prev,
          message,
          isConnected: false,
        }));
        return null;
      } finally {
        setIsGenerating(false);
      }
    },
    [getAccessToken]
  );

  const reset = useCallback(() => {
    setProgress(initialProgress);
    setError(null);
    setIsGenerating(false);
  }, []);

  return {
    progress,
    isGenerating,
    error,
    generateDraft,
    reset,
  };
}
